import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Typography from '@material-ui/core/Typography';
import { Colors } from '@silvergatedelivery/constants';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Loading from 'components/Loading';
import DateRangeSelector from 'components/DateRangeSelector';
import MealStatisticsTable from './components/MealStatisticsTable';
import RestaurantMealTagSelect from './components/RestaurantMealTagSelect';
import MealStatisticsSettingsToolbar from './components/MealStatisticsSettingsToolbar';
import { getDeliveryStaffIdSchema } from 'forms/schemas';
import {
  getOrders,
  getStartEndByPeriodName,
} from './helpers';
import { asyncListAll } from 'utilities/graph';
import cache from 'utilities/cache';
import RestaurantMealTagSelectDialog from './components/RestaurantMealTagSelectDialog';
import OrderTable from 'components/OrderTable';
import { sortOrdersByDeliveryGroups } from 'utilities/sorting';
import { getElderDeliveryGroupsByClientByStatus } from './queries';

const mealSlotOptions = [{
  label: '午餐',
  value: 'lunch',
}, {
  label: '晚餐',
  value: 'dinner',
}];

const periodOptions = [{
  label: '本日',
  value: 'day',
}, {
  label: '昨日',
  value: 'day/-1/-1',
}, {
  label: '明日',
  value: 'day/1/1',
}];

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    backgroundColor: Colors.background.light,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    marginBottom: 12,
  },
  buttonGroup: {
    'display': 'flex',
    'flexDirection': 'column',
    'alignItems': 'center',
    '& > *': {
      margin: theme.spacing(1),
    },
    'marginLeft': 36,
  },
}));

export default function MealStatistics({ clientId: inClientId }) {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [period, setPeriod] = useState('day'); // day, week, month, year
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [progressDate, setProgressDate] = useState('');
  const [mealSlots, setMealSlots] = useState([]);
  const [deliveryStaffs, setDeliveryStaffs] = useState([]);
  const [orders, setOrders] = useState([]);
  // const [restaurantMealOptions, setRestaurantMealOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [restaurantMealTagList, setRestaurantMealTagList] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [displayOrderIds, setDisplayOrderIds] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState();
  const [showElders, setShowElders] = useState(false);
  const [showEldersAndTags, setShowEldersAndTags] = useState(false);
  const [showEldersWithTag, setShowEldersWithTag] = useState(false);
  const [showDeliveryOrderOption, setShowDeliveryOrderOption] = useState(false);
  const [transposeCsv, setTransposeCsv] = useState(false);
  const [showMealItemName, setShowMealItemName] = useState(false);
  const [deliveryStaffsWithOrders, setDeliveryStaffsWithOrders] = useState([]);
  const [selectedDeliveryStaffIds, setSelectedDeliveryStaffIds] = useState([]);
  const [showDeliveryStaffFilter, setShowDeliveryStaffFilter] = useState(false);

  const clientId = inClientId || cache.get('app:facilityId');

  const setCustomPeriod = ({ startDate, endDate }) => {
    setPeriod(`month/0/0/${startDate}/${endDate}`);
  };

  const processOrders = (orders) => {
    const data = [];
    const selectedDeliveryStaffs = deliveryStaffsWithOrders.filter(({ id }) => selectedDeliveryStaffIds.includes(id));
    const showDeliveryOrder = showDeliveryOrderOption && mealSlots.length === 1 && startDate === endDate;
    let rowIndex = 0;
    restaurantMealTagList.forEach(({ restaurantId, restaurantName, mealItemName, tags }) => {
      const deliveryStaffMealCountList = selectedDeliveryStaffs.map(({ name, id }) => ({
        id,
        name,
        count: 0,
        orderIds: [],
        orders: [],
        elders: [],
        mealItemName: mealItemName,
      }));
      const row = {
        index: rowIndex,
        restaurantId: restaurantId,
        restaurantName: restaurantName,
        mealItemName: mealItemName,
        tags,
        deliveryStaffMealCountList,
        total: 0,
        orderIds: [],
        orders: [],
      };
      rowIndex += 1;
      data.push(row);
    });
    orders.forEach((order) => {
      order.mealItems.forEach((mealItem) => {
        const index = data.findIndex(({ restaurantId, mealItemName, tags }) => {
          let tagMatched = true;
          if (tags && tags.length !== 0) {
            if (order.elder?.tags?.items) {
              const elderTags = order.elder?.tags?.items.filter(({ tag }) => tag.subcategory === '飲食').map(({ tag }) => tag.label);
              if (!tags.every((tag) => elderTags.includes(tag))) {
                tagMatched = false;
              }
            }
          }
          return restaurantId === order.restaurantId && mealItem.name === mealItemName && tagMatched;
        });
        if (index === -1) {
          const deliveryStaffMealCountList = selectedDeliveryStaffs.map(({ name, id }) => ({
            id,
            name,
            count: 0,
            orderIds: [],
            orders: [],
            elders: [],
            mealItemName: mealItem.name,
          }));
          const row = {
            index: rowIndex,
            restaurantId: order.restaurantId,
            restaurantName: order.restaurant.name,
            mealItemName: mealItem.name,
            deliveryStaffMealCountList,
            total: mealItem.quantity,
            orderIds: [],
            orders: [],
          };
          const deliveryStaffIndex = row.deliveryStaffMealCountList.findIndex(({ id }) => {
            return order.deliveryStaffId ? id === order.deliveryStaffId : id === 'N/A';
          });
          row.deliveryStaffMealCountList[deliveryStaffIndex].count += mealItem.quantity;
          if (!row.orderIds.includes(order.id)) {
            row.orderIds.push(order.id);
            row.orders.push(order);
          }
          if (!row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.includes(order.id)) {
            row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.push(order.id);
            row.deliveryStaffMealCountList[deliveryStaffIndex].orders.push(order);
            if (order.elder) {
              if (!row.deliveryStaffMealCountList[deliveryStaffIndex].elders.some(({ id }) => id === order.elder.id)) {
                row.deliveryStaffMealCountList[deliveryStaffIndex].elders.push({
                  ...order.elder, deliveryOrder: showDeliveryOrder && order.deliveryOrder,
                });
              }
            }
          }
          rowIndex += 1;
          data.push(row);
        } else {
          const row = data[index];
          const deliveryStaffIndex = row.deliveryStaffMealCountList.findIndex(({ id }) => {
            return order.deliveryStaffId ? id === order.deliveryStaffId : id === 'N/A';
          });
          row.total += mealItem.quantity;
          row.deliveryStaffMealCountList[deliveryStaffIndex].count += mealItem.quantity;
          if (!row.orderIds.includes(order.id)) {
            row.orderIds.push(order.id);
            row.orders.push(order);
          }
          if (!row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.includes(order.id)) {
            row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.push(order.id);
            row.deliveryStaffMealCountList[deliveryStaffIndex].orders.push(order);
            if (order.elder) {
              if (!row.deliveryStaffMealCountList[deliveryStaffIndex].elders.some(({ id }) => id === order.elder.id)) {
                row.deliveryStaffMealCountList[deliveryStaffIndex].elders.push({
                  ...order.elder, deliveryOrder: showDeliveryOrder && order.deliveryOrder,
                });
              }
            }
          }
        }
      });
    });
    const dataWithTag = data.slice(0, restaurantMealTagList.length);
    const dataWithoutTag = data.slice(restaurantMealTagList.length).sort((a, b) => {
      if (a.restaurantName === b.restaurantName) {
        return a.mealItemName > b.mealItemName ? 1 : -1;
      }
      return a.restaurantName > b.restaurantName ? 1 : -1;
    });
    rowIndex = restaurantMealTagList.length;
    dataWithoutTag.forEach((row) => {
      row.index = rowIndex;
      rowIndex += 1;
    });
    setData([...dataWithTag, ...dataWithoutTag]);
  };

  useEffect(() => {
    if (!clientId) return;

    (async () => {
      setIsLoading(true);

      const {
        startFormat,
        endFormat,
      } = getStartEndByPeriodName(period);

      setStartDate(startFormat);
      setEndDate(endFormat);

      const onUpdateLastItem = (item, nextToken) => {
        if (nextToken) {
          if (item) {
            setProgressDate(item.date);
          }
        } else {
          setProgressDate('');
        }
      };

      const elderDeliveryGroups = await asyncListAll(getElderDeliveryGroupsByClientByStatus, {
        clientId: clientId,
        status: { eq: '使用中' },
        limit: 1000,
      });

      let orders = (await getOrders(clientId, startFormat, endFormat, onUpdateLastItem))
        .filter(({ status }) => status !== 'cancelled');
      orders = sortOrdersByDeliveryGroups(orders, elderDeliveryGroups);

      let allDeliveryStaffs = deliveryStaffs;
      if (deliveryStaffs.length === 0) {
        // TODO: 有可能會少掉已經停用的大使
        allDeliveryStaffs = await getDeliveryStaffIdSchema(null, null, clientId,
          true, true, true, true, false);
        setDeliveryStaffs(allDeliveryStaffs);
      }

      const tagOptions = [];
      orders.forEach(({ elder }) => {
        if (elder?.tags?.items) {
          elder?.tags?.items.forEach(({ tag }) => {
            if (tag.subcategory === '飲食') {
              if (tagOptions.findIndex(({ id }) => id === tag.id) === -1) {
                tagOptions.push({
                  id: tag.id,
                  label: tag.label,
                });
              }
            }
          });
        }
      });
      setOrders(orders);
      // setRestaurantMealOptions(restaurantMealOptions);
      setTagOptions(tagOptions);
      setIsLoading(false);
    })();
  }, [period, clientId, lastUpdatedAt]);

  useEffect(() => {
    const staffIdSet = new Set();
    orders.forEach((order) => {
      if (order.deliveryStaffId) {
        staffIdSet.add(order.deliveryStaffId);
      } else {
        staffIdSet.add('N/A');
      }
    });
    const filtered = deliveryStaffs.filter((staff) => staffIdSet.has(staff.id));
    staffIdSet.forEach((id) => {
      if (id !== 'N/A' && !filtered.find((staff) => staff.id === id)) {
        filtered.push({ id: id, name: '已停用或停止合作的大使' });
      }
    });
    if (staffIdSet.has('N/A')) {
      filtered.push({ id: 'N/A', name: '未指定送餐大使' });
    }
    setDeliveryStaffsWithOrders(filtered);
    setSelectedDeliveryStaffIds(filtered.map((staff) => staff.id));
  }, [orders, deliveryStaffs]);

  useEffect(() => {
    const filteredOrders = orders
      .filter(({ deliveryStaffId }) => selectedDeliveryStaffIds.includes(deliveryStaffId) ||
        (!deliveryStaffId && selectedDeliveryStaffIds.includes('N/A')));
    if (mealSlots.length === 0 || mealSlots.length === 2) {
      processOrders(filteredOrders);
    } else {
      const slotFilteredOrders = filteredOrders.filter(({ mealSlot }) => mealSlots[0] === mealSlot);
      processOrders(slotFilteredOrders);
    }
  }, [mealSlots, orders, restaurantMealTagList, showDeliveryOrderOption, selectedDeliveryStaffIds]);

  const onAddRestaurantMealTag = (value) => {
    setRestaurantMealTagList([...restaurantMealTagList, value]);
    setDirty(true);
  };

  const onUpdateRestaurantMealTag = (value) => {
    setRestaurantMealTagList(value);
    setDirty(false);
  };

  const onRowUp = (index) => {
    if (index > 0 && index < restaurantMealTagList.length) {
      [restaurantMealTagList[index - 1], restaurantMealTagList[index]] = [restaurantMealTagList[index], restaurantMealTagList[index - 1]];
      setRestaurantMealTagList([...restaurantMealTagList]);
      setDirty(true);
    }
  };

  const onRowDown = (index) => {
    if (index >= 0 && index < restaurantMealTagList.length - 1) {
      [restaurantMealTagList[index], restaurantMealTagList[index + 1]] = [restaurantMealTagList[index + 1], restaurantMealTagList[index]];
      setRestaurantMealTagList([...restaurantMealTagList]);
      setDirty(true);
    }
  };

  const onRowEdit = (value) => {
    setSelectedIndex(value);
    setShowEditDialog(true);
  };

  const onRowDelete = (index) => {
    if (window.confirm('確認刪除該項目?')) {
      const newRestaurantMealTagList = restaurantMealTagList.filter((_, i) => i !== index);
      setRestaurantMealTagList(newRestaurantMealTagList);
      setDirty(true);
    }
  };

  return (
    <Container maxWidth={false} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.filterContainer}>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12}>
                <MealStatisticsSettingsToolbar restaurantMealTagList={restaurantMealTagList} onUpdate={onUpdateRestaurantMealTag} dirty={dirty}/>
              </Grid>
              <Grid item xs={12} container spacing={2} alignItems="center">
                <DateRangeSelector
                  startDate={startDate}
                  endDate={endDate}
                  onUpdate={setCustomPeriod}
                />
                <div className={classes.buttonGroup}>
                  <ButtonGroup color="primary" aria-label="outlined primary button group">
                    {mealSlotOptions.map((item, index)=>(
                      <Button
                        key={index}
                        disabled={isLoading}
                        variant={mealSlots.includes(item.value) ? 'contained':'outlined'}
                        onClick={() => {
                          if (mealSlots.includes(item.value)) {
                            const index = mealSlots.indexOf(item.value);
                            mealSlots.splice(index, 1);
                            setMealSlots([...mealSlots]);
                          } else {
                            mealSlots.push(item.value);
                            setMealSlots([...mealSlots]);
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
                <div className={classes.buttonGroup}>
                  <ButtonGroup color="primary" aria-label="outlined primary button group">
                    {periodOptions.map((item, index)=>(
                      <Button
                        key={index}
                        disabled={isLoading}
                        variant={period === item.value ? 'contained':'outlined'}
                        onClick={() => setPeriod(item.value)}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
                <div style={{ marginLeft: 36 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showElders}
                        value={showElders}
                        onChange={(e) => {
                          setShowElders(e.target.checked);
                          if (e.target.checked) {
                            if (showEldersWithTag) {
                              setShowEldersWithTag(false);
                            }
                            if (showEldersAndTags) {
                              setShowEldersAndTags(false);
                            }
                          }
                        }}
                        name={'showElders'}
                        color="primary"
                      />
                    }
                    label="顯示所有送餐對象"
                  />
                </div>
                <div style={{ marginLeft: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showEldersAndTags}
                        value={showEldersAndTags}
                        onChange={(e) => {
                          setShowEldersAndTags(e.target.checked);
                          if (e.target.checked) {
                            setShowEldersWithTag(false);
                            if (showElders) {
                              setShowElders(false);
                            }
                            if (showEldersWithTag) {
                              setShowEldersWithTag(false);
                            }
                          }
                        }}
                        name={'showEldersAndTags'}
                        color="primary"
                      />
                    }
                    label="顯示所有飲食標籤與送餐對象"
                  />
                </div>
                <div style={{ marginLeft: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showEldersWithTag}
                        value={showEldersWithTag}
                        onChange={(e) => {
                          setShowEldersWithTag(e.target.checked);
                          if (e.target.checked) {
                            if (showElders) {
                              setShowElders(false);
                            }
                            if (showEldersAndTags) {
                              setShowEldersAndTags(false);
                            }
                          }
                        }}
                        name={'showEldersWithTag'}
                        color="primary"
                      />
                    }
                    label="顯示有飲食標籤的送餐對象"
                  />
                </div>
                <div style={{ marginLeft: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showMealItemName}
                        onChange={(e) => setShowMealItemName(e.target.checked)}
                        name={'showMealItemName'}
                        color="primary"
                      />
                    }
                    label="數量旁顯示餐點名稱"
                  />
                </div>
                <div style={{ marginLeft: 8 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transposeCsv}
                        onChange={(e) => setTransposeCsv(e.target.checked)}
                        name={'transposeCsv'}
                        color="primary"
                      />
                    }
                    label="下載CSV時切換列/欄"
                  />
                </div>
                <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showDeliveryStaffFilter}
                        onChange={(e) => {
                          setShowDeliveryStaffFilter(e.target.checked);
                          if (!e.target.checked) {
                            setSelectedDeliveryStaffIds(deliveryStaffsWithOrders.map((staff) => staff.id));
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="套用送餐大使篩選"
                  />
                </div>
                {mealSlots.length === 1 && startDate === endDate && (
                  <div style={{ marginLeft: 8 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showDeliveryOrderOption}
                          onChange={(e) => setShowDeliveryOrderOption(e.target.checked)}
                          name={'showSortDeliveryOrderOption'}
                          color="primary"
                        />
                      }
                      label="顯示送餐順序"
                    />
                  </div>
                )}
              </Grid>
              <Grid item xs={12}>
                <RestaurantMealTagSelect tagOptions={tagOptions} onUpdate={onAddRestaurantMealTag}/>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Grid container>
        {isLoading &&
          <>
            <Loading fullScreen={false} />
            <Typography variant="body1" color="textPrimary" style={{ flex: 1, textAlign: 'center' }}>
              {progressDate && `讀取進度：${progressDate}，請稍候...`}
            </Typography>
          </>}
        {!isLoading && showDeliveryStaffFilter &&
          <Grid item xs={12}>
            <Paper elevation={3} style={{ padding: 16, margin: '16px 0', width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
              <Typography variant="h6" style={{ marginBottom: 12, fontWeight: 600 }}>
                送餐大使篩選
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ marginBottom: 8 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedDeliveryStaffIds(deliveryStaffsWithOrders.map((staff) => staff.id))}
                    style={{ marginRight: 8 }}
                  >
                    全選
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedDeliveryStaffIds([])}
                  >
                    全部取消
                  </Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {deliveryStaffsWithOrders.map((staff) => (
                    <FormControlLabel
                      key={staff.id}
                      control={
                        <Checkbox
                          checked={selectedDeliveryStaffIds.includes(staff.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDeliveryStaffIds([...selectedDeliveryStaffIds, staff.id]);
                            } else {
                              setSelectedDeliveryStaffIds(selectedDeliveryStaffIds.filter((id) => id !== staff.id));
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={staff.name}
                      style={{ minWidth: 160 }}
                    />
                  ))}
                </div>
              </div>
            </Paper>
          </Grid>
        }
        {!isLoading &&
          <Grid item xs={12}>
            <MealStatisticsTable
              data={data}
              orderDateInfo={{
                startDate,
                endDate,
                mealSlots,
              }}
              onRowUp={onRowUp}
              onRowDown={onRowDown}
              onRowEdit={onRowEdit}
              onRowDelete={onRowDelete}
              onCellClick={(value) => setDisplayOrderIds(value?.orderIds || [])}
              onRefresh={() => {
                setLastUpdatedAt(Date.now());
              }}
              showElders={showElders}
              showEldersAndTags={showEldersAndTags}
              showEldersWithTag={showEldersWithTag}
              transposeCsv={transposeCsv}
              showMealItemName={showMealItemName}
            />
          </Grid>
        }
        {displayOrderIds.length !== 0 &&
          <Grid item xs={12}>
            <OrderTable title='餐點訂單' showElderTags orderIds={displayOrderIds} />
          </Grid>
        }
      </Grid>
      <RestaurantMealTagSelectDialog
        open={showEditDialog}
        tagOptions={tagOptions}
        data={restaurantMealTagList[selectedIndex]}
        onClose={() => {
          setShowEditDialog(false);
        }}
        onUpdate={(value) => {
          restaurantMealTagList[selectedIndex] = value;
          setRestaurantMealTagList([...restaurantMealTagList]);
          setDirty(true);
        }}
      />
    </Container>
  );
}

MealStatistics.propTypes = {
  clientId: PropTypes.string,
};
